import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IOrganization, IUser } from "../../../model/model";
import axios from "axios";
import apiUrls from "../../../api";
import { storeAuthInLocalStorage } from "../../../common/util/localStorage";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import { ZendeskAPI } from "react-zendesk";
import { clearProfile } from "../../profile/state/profileSlice";
import type { IProjectGroupWithProjects } from "../../projectGroups/state/projectGroupsSlice";
import {
  clearProjectGroups,
  setProjectGroupsWithProjects,
} from "../../projectGroups/state/projectGroupsSlice";
import { clearProjects } from "../../graph/state/projectsSliceLegacy";
import { clearGallery } from "../../gallery/state/gallerySlice";
import { clearTeam } from "../../team/state/teamSlice";
import { clearAlerts } from "../../alert/state/alertsSlice";
import type { AppDispatch, RootState } from "src/common/state/store";
import { destroyHelpDocsWidget } from "src/common/util/helpDocsLighthouse/destroyHelpDocsWidget";
import { clearDeviceInstallerState } from "../../deviceInstaller/state/deviceInstallerSlice";
import { clearBuilds } from "../../builds/state/buildsSlice";

interface ILegals {
  tos: { s3_url: string };
  privacyPolicy: { s3_url: string };
}

export interface IAuthFormData {
  email: string;
  password: string;
  from_environment?: string;
  rememberme: boolean;
}

// docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 7 step 1/3 + Appendix A.
// `method` is null only for a first-time mandated-enrollment challenge (leg 1
// hasn't picked a factor yet - the user chooses it in the enroll wizard).
export type MfaChallengeRequirement = "mfa_required" | "mfa_enrollment_required";

export interface IMfaChallenge {
  token: string;
  method: "totp" | "email" | null;
  requirement: MfaChallengeRequirement;
  /** Email method only (null for TOTP): the address the code was sent to, so
   * the challenge screen can name the mailbox to check rather than saying
   * "your email". Shown in full, not masked — creators sign in with their
   * email address, so this is the value they typed on the previous screen.
   * See check-mfa-required.js for the reasoning and the condition that would
   * reverse it. */
  sentTo: string | null;
}

export interface IForgottenPasswordFormData {
  email: string;
}

export interface IResetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

export interface ISetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

interface IAuth {
  jwt: string;
  helpDocsJwt: string;
  userId?: number;
  currentCommitHash: string;
  legals: ILegals;
  mfaChallenge: IMfaChallenge | null;
}

const initialState: IAuth = {
  jwt: "",
  helpDocsJwt: "",
  currentCommitHash: "",
  legals: {
    tos: {
      s3_url: "",
    },
    privacyPolicy: {
      s3_url: "",
    },
  },
  mfaChallenge: null,
};

export const signIn = createAsyncThunk(
  "auth/login",
  async (
    { email, password, rememberme, from_environment = "WebApp" }: IAuthFormData & IAppLoaderAction,
    { dispatch },
  ) => {
    const response = await axios.post<{
      jwt: string | null;
      helpDocsJwt?: string;
      user: IUser | null;
      firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
      // docs/plans/todo/creator-mfa-two-factor-auth.md Appendix A - leg 1.
      // HTTP 200 with jwt/user null and these three set instead means the
      // login isn't finished: the credentials were correct, but a second
      // step (challenge or forced enrollment) is required.
      status?: { error: string | null };
      mfa_token?: string;
      mfa_method?: "totp" | "email" | null;
      mfa_sent_to?: string | null;
    }>(
      `${apiUrls.auth.signIn}?populate=*&populate[organization][populate][admin]=*&populate[organization][populate][source_logo]=*`,
      {
        identifier: email,
        password,
        from_environment,
        remember_me: rememberme,
      },
    );

    const {
      status,
      mfa_token: mfaToken,
      mfa_method: mfaMethod,
      mfa_sent_to: mfaSentTo,
    } = response.data;

    if (status?.error === "mfa_required" || status?.error === "mfa_enrollment_required") {
      // Store NOTHING - Anti-Goals: the mfa_token is Redux-only, never
      // localStorage, and lives only for the duration of this login.
      return {
        jwt: null,
        helpDocsJwt: null,
        mfaChallenge: {
          token: mfaToken as string,
          method: mfaMethod ?? null,
          sentTo: mfaSentTo ?? null,
          requirement: status.error as MfaChallengeRequirement,
        },
      };
    }

    const { jwt, helpDocsJwt, user, firstTwoProjectGroupsWithProjects } = response.data;
    const organization = (user as IUser).organization;

    if (
      Array.isArray(firstTwoProjectGroupsWithProjects) &&
      firstTwoProjectGroupsWithProjects.length > 0
    ) {
      dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
    }
    // Storing jwt and user in local storage
    await storeAuthInLocalStorage(
      user as IUser,
      organization,
      jwt as string,
      helpDocsJwt as string,
      true,
    );

    return { jwt, helpDocsJwt, mfaChallenge: null };
  },
);

// docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 7 step 2. Leg 2 of the
// MFA login flow - on success this does exactly what signIn does today
// (store auth, hydrate the first project groups) and the caller never has to
// know it took two requests instead of one.
export const verifyMfa = createAsyncThunk(
  "auth/verifyMfa",
  async (
    {
      mfaToken,
      otp,
      recoveryCode,
    }: { mfaToken: string; otp?: string; recoveryCode?: string } & IAppLoaderAction,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{
        jwt: string;
        helpDocsJwt: string;
        user: IUser;
        firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
        recovery_codes_remaining?: number;
      }>(
        apiUrls.mfa.verify,
        { mfa_token: mfaToken, otp, recovery_code: recoveryCode },
        {
          // The single highest-risk detail of this phase (see the plan's Phase
          // 4 step 5b / Phase 7 step 7): installAuthHeaderFallback attaches
          // any non-expired stored JWT to every request lacking an
          // Authorization header. /mfa/verify is mfa_token-only server-side
          // (resolveMfaActor allowJwt: false) so a stale header can't hijack
          // the login, but it still rides along and pollutes the request.
          // Send an explicit empty header to defeat the interceptor outright.
          headers: { Authorization: undefined },
        },
      );

      const {
        jwt,
        helpDocsJwt,
        user,
        firstTwoProjectGroupsWithProjects,
        recovery_codes_remaining: recoveryCodesRemaining,
      } = response.data;
      const organization = user.organization;

      if (
        Array.isArray(firstTwoProjectGroupsWithProjects) &&
        firstTwoProjectGroupsWithProjects.length > 0
      ) {
        dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
      }
      await storeAuthInLocalStorage(user, organization, jwt, helpDocsJwt, true);

      return { jwt, helpDocsJwt, recoveryCodesRemaining: recoveryCodesRemaining ?? null };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // D1: surface the real HTTP status alongside the Strapi error body
        // (reportsSlice.ts precedent) so MfaChallengeForm can branch on 429
        // (rate limited - limitMfaVerify, 10/15min) instead of taking every
        // rejection at face value and blaming the code the user typed.
        return rejectWithValue({
          ...(err.response?.data as Record<string, unknown> | undefined),
          status: err.response?.status ?? null,
        });
      }
      throw err;
    }
  },
);

// Resend the email OTP for a pending "verify" challenge. Always resolves
// {ok: true} server-side (Appendix E) - failure here means a network/HTTP
// problem, not "no code was sent", so the generic error path is enough.
export const resendMfaChallenge = createAsyncThunk(
  "auth/resendMfaChallenge",
  async ({ mfaToken }: { mfaToken: string } & IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ ok: boolean }>(
        apiUrls.mfa.challenge,
        { mfa_token: mfaToken },
        // Same interceptor-defeat as verifyMfa above - /mfa/challenge is also
        // mfa_token-only (allowJwt: false).
        { headers: { Authorization: undefined } },
      );
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const getEditorWebLastCommit = createAsyncThunk("auth/getEditorWebLastCommit", async () => {
  const response = await axios.get(apiUrls.getEditorWebLastCommit);
  return { legals: response.data };
});

export const fetchLegals = createAsyncThunk(
  "auth/fetchLegals",
  async (_: IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.get<ILegals>(apiUrls.legals);
      return { legals: response.data };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const forgottenPassword = createAsyncThunk(
  "auth/forgottenPassword",
  async ({ email }: IForgottenPasswordFormData & IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.forgetPassword, {
        email,
      });
      // window.location.replace("/");
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: IResetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.resetPassword, {
        code: code,
        password: password,
        passwordConfirmation: passwordConfirmation,
      });
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

export const setInitialPassword = createAsyncThunk(
  "auth/setInitialPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: ISetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      // CRITICAL: Remove Authorization header for this unauthenticated endpoint
      // User may be logged in when testing invite links, but this endpoint
      // authenticates via the resetPasswordToken in the request body, not JWT
      const response = await axios.post<{ jwt: string; user: IUser }>(
        `${apiUrls.base}/api/auth/set-initial-password`,
        {
          data: { code, password, passwordConfirmation },
        },
        {
          headers: {
            Authorization: undefined, // Explicitly remove the JWT header
          },
        },
      );
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // Map backend error messages to translation keys
        const errorMessage = err?.response?.data?.error?.message || "An error occurred";

        // Map known backend errors to translation keys
        const errorMap: { [key: string]: string } = {
          "Missing required fields": "pages.setPassword.errors.missingFields",
          "Passwords do not match": "pages.setPassword.errors.passwordsDoNotMatch",
          "Password requirements not met": "pages.setPassword.errors.passwordRequirementsNotMet",
          "Invalid or already used token": "pages.setPassword.errors.invalidOrUsedToken",
        };

        const translationKey = errorMap[errorMessage] || "pages.setPassword.errors.genericError";

        // Return translation key instead of raw error message
        return rejectWithValue({
          error: {
            message: translationKey,
          },
        });
      } else {
        throw err;
      }
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (
      state: IAuth,
      action: PayloadAction<{ jwt: string; user: IUser; organization: IOrganization }>,
    ) => {
      state.jwt = action.payload.jwt;
    },
    resetJwt: (state: IAuth) => {
      state.jwt = "";
      state.mfaChallenge = null;
      axios.defaults.headers.common["Authorization"] = undefined;
      window.localStorage.clear();
    },
    setCurrentCommitHash: (state: IAuth, action: PayloadAction<string>) => {
      state.currentCommitHash = action.payload;
    },
    // docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 7 step 3/6. Returns
    // the login screen to the credentials form: dispatched on cancel, on a
    // "mfa_challenge_invalid" (expired token) response, and by resetJwt/
    // logoutAuth so a stale challenge never survives a logout.
    clearMfaChallenge: (state: IAuth) => {
      state.mfaChallenge = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      if (payload.mfaChallenge) {
        // Credentials were correct but the login isn't finished - do NOT
        // touch state.jwt (it must stay falsy so LoginForm's [jwt] navigate
        // effect never fires) and never write anything to localStorage.
        state.mfaChallenge = payload.mfaChallenge;
        return;
      }
      state.jwt = payload.jwt as string;
      state.helpDocsJwt = payload.helpDocsJwt as string;
      state.mfaChallenge = null;
    });
    builder.addCase(verifyMfa.fulfilled, (state, { payload }) => {
      state.jwt = payload.jwt;
      state.helpDocsJwt = payload.helpDocsJwt;
      state.mfaChallenge = null;
    });
    builder.addCase(fetchLegals.fulfilled, (state, { payload }) => {
      state.legals = payload.legals;
    });
  },
});

export const authReducer = authSlice.reducer;

export const getHelpDocsJwt = (state: RootState) => {
  return state.auth.helpDocsJwt;
};

export const getLegals = (state: IAuth) => state.legals;

export const logoutAuth = () => (dispatch: AppDispatch) => {
  ZendeskAPI("webWidget", "hide");
  dispatch(clearProfile());
  dispatch(clearProjectGroups());
  dispatch(clearProjects());
  dispatch(clearGallery());
  dispatch(clearTeam());
  dispatch(clearAlerts());
  dispatch(clearDeviceInstallerState());
  dispatch(clearBuilds());
  // We don't want to discard localStorage.CURRENT_COMMIT_HASH
  const CURRENT_COMMIT_HASH = localStorage.getItem("CURRENT_COMMIT_HASH");
  dispatch(authSlice.actions.resetJwt());
  if (CURRENT_COMMIT_HASH) {
    localStorage.setItem("CURRENT_COMMIT_HASH", CURRENT_COMMIT_HASH);
  }

  destroyHelpDocsWidget();
};

export const { setAuth, clearMfaChallenge } = authSlice.actions;
